import React, { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Download, FileSpreadsheet, FileText, CalendarRange, Loader2 } from 'lucide-react';
import { Button, Empty, ErrorBlock, Skeleton, downloadFile, errMsg, fmtDate, money, useLoad } from '../kit';
import { API, PeriodPicker, calendarPresets, rangeLabel } from './shared';
import TimeCaseNotesReport from './TimeCaseNotesReport';

// Reports: "give me the file". The library is grouped by area; picking a report shows what it holds,
// its period (or "as at today"), the first rows, and the CSV and Excel downloads of the whole thing.
// Time and Case Notes (custom: true) has its own filters and screen (TimeCaseNotesReport); `tcn`
// carries the participants and dates it was opened with from the address.

const PREVIEW_ROWS = 10;
const NUMERIC = ['money', 'number', 'hours'];

export default function ReportsTab({ selected, onSelect, period, onPeriod, tcn }) {
  const lib = useLoad(`${API}/reports`);
  const areas = useMemo(() => (lib.data && lib.data.areas) || [], [lib.data]);
  const all = useMemo(() => areas.flatMap((a) => a.reports), [areas]);
  const current = all.find((r) => r.key === selected) || all[0] || null;
  const previewRef = useRef(null);
  const choose = (key) => {
    onSelect(key);
    // Phones: the preview sits under the library, so bring it into view.
    if (previewRef.current && window.matchMedia && !window.matchMedia('(min-width: 1024px)').matches && previewRef.current.scrollIntoView) {
      previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (lib.error && !lib.data) return <ErrorBlock error={lib.error} onRetry={lib.reload} />;
  if (!lib.data) return <LibrarySkeleton />;
  if (!all.length) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200/80">
        <Empty icon={FileText} title="No reports for your permissions">Ask an admin for View all hours or Claiming to see the reports for those areas.</Empty>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,22rem)_minmax(0,1fr)] gap-5 items-start">
      <nav aria-label="Report library" className="bg-white rounded-2xl border border-slate-200/80 py-2 lg:sticky lg:top-4">
        {areas.map((a) => (
          <div key={a.key} className="px-2 py-1.5">
            <h2 className="px-3 pt-1.5 pb-1 text-[12.5px] font-semibold text-violet-800">{a.label}</h2>
            <ul>
              {a.reports.map((r) => {
                const on = current && current.key === r.key;
                return (
                  <li key={r.key}>
                    <button type="button" onClick={() => choose(r.key)} aria-current={on ? 'true' : undefined}
                      className={clsx('w-full text-left rounded-xl px-3 py-2.5 min-h-[44px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                        on ? 'bg-violet-50 ring-1 ring-inset ring-violet-200' : 'hover:bg-slate-50')}>
                      <span className={clsx('block text-[13.5px] font-semibold leading-5', on ? 'text-violet-900' : 'text-slate-900')}>{r.label}</span>
                      <span className="block text-[12.5px] text-slate-600 leading-[18px] mt-0.5">{r.desc}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      <div ref={previewRef} className="scroll-mt-4 min-w-0">
        {current && (current.custom
          ? <TimeCaseNotesReport key={current.key} initialClientIds={tcn && tcn.clientIds} initialFrom={tcn && tcn.from} initialTo={tcn && tcn.to} />
          : <ReportPreview key={current.key} report={current} period={period} onPeriod={onPeriod} />)}
      </div>
    </div>
  );
}

function ReportPreview({ report, period, onPeriod }) {
  const params = report.period ? { from: period.from, to: period.to, limit: PREVIEW_ROWS } : { limit: PREVIEW_ROWS };
  const { data, loading, error, reload } = useLoad(`${API}/reports/${report.key}`, params);
  const [busy, setBusy] = useState(null);
  const presets = useMemo(() => calendarPresets(), []);
  // A stale preview never shows under a different report's heading.
  const fresh = data && data.key === report.key ? data : null;

  const download = async (format) => {
    setBusy(format);
    try {
      const name = await downloadFile(`${API}/reports/${report.key}`, { params: { ...(report.period ? { from: period.from, to: period.to } : {}), format } });
      toast.success(`Downloaded ${name}`);
    } catch (e) { toast.error(errMsg(e, 'Couldn’t make the file. Please try again.')); }
    finally { setBusy(null); }
  };

  return (
    <section aria-labelledby="rep-h" className="bg-white rounded-2xl border border-slate-200/80">
      <div className="flex flex-wrap items-start justify-between gap-3 px-4 sm:px-5 pt-4 pb-3">
        <div className="min-w-0">
          <h2 id="rep-h" className="text-[16px] font-semibold text-slate-900 leading-6">{report.label}</h2>
          <p className="text-[13px] text-slate-600 mt-0.5 max-w-xl">{report.desc}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" icon={Download} onClick={() => download('csv')} loading={busy === 'csv'} disabled={!!busy}
            aria-label={`Download ${report.label} as CSV`}>CSV</Button>
          <Button variant="secondary" icon={FileSpreadsheet} onClick={() => download('xlsx')} loading={busy === 'xlsx'} disabled={!!busy}
            aria-label={`Download ${report.label} as Excel`}>Excel</Button>
        </div>
      </div>
      <div className="px-4 sm:px-5 pb-3 border-b border-slate-100">
        {report.period
          ? <PeriodPicker value={period} onChange={onPeriod} presets={presets} />
          : <p className="inline-flex items-center gap-1.5 text-[13px] text-slate-600"><CalendarRange size={14} aria-hidden /> As at today: this report has no period.</p>}
      </div>
      <div className="px-4 sm:px-5 py-4">
        {error && !fresh ? <ErrorBlock error={error} onRetry={reload} />
          : !fresh ? <div className="space-y-2" aria-busy="true" aria-label="Loading">{[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-8" />)}</div>
            : (
              <>
                <p className="text-[13px] text-slate-600 mb-3 flex items-center gap-2" aria-live="polite">
                  {fresh.total === 0 ? 'No rows' : fresh.total > fresh.rows.length ? `First ${fresh.rows.length} of ${fresh.total} rows` : `${fresh.total} ${fresh.total === 1 ? 'row' : 'rows'}`}
                  {report.period && fresh.from && <span className="text-slate-500">· {rangeLabel(fresh.from, fresh.to)}</span>}
                  {loading && <Loader2 size={13} className="animate-spin text-slate-500" aria-label="Updating" />}
                </p>
                {fresh.total === 0
                  ? <p className="text-[13px] text-slate-600 py-6 text-center">{report.period ? 'Nothing in this period. Try a longer period.' : 'Nothing to report right now.'}</p>
                  : <PreviewTable data={fresh} />}
                {fresh.total > fresh.rows.length && <p className="mt-3 text-[12.5px] text-slate-500">Download the CSV or Excel file for all {fresh.total} rows.</p>}
              </>
            )}
      </div>
    </section>
  );
}

const cellText = (v, t) => {
  if (v == null || v === '') return '—';
  if (t === 'money') return money(v);
  if (t === 'hours') return `${Number(v).toFixed(2)} h`;
  if (t === 'date') return fmtDate(v);
  if (t === 'number') return Number(v).toLocaleString('en-AU');
  return String(v);
};

function PreviewTable({ data }) {
  const types = data.types || {};
  return (
    // Scrolls inside its panel on narrow screens; the page never scrolls sideways.
    <div className="overflow-x-auto -mx-1 px-1 pb-1">
      <table className="w-full text-[13px] border-separate border-spacing-0">
        <caption className="sr-only">{data.label}: preview</caption>
        <thead>
          <tr>
            {data.columns.map((c) => (
              <th key={c} scope="col" className={clsx('bg-slate-50/95 text-[12px] font-semibold text-slate-600 border-y border-slate-200/80 px-3 py-2 first:pl-3 first:rounded-l-lg last:rounded-r-lg whitespace-nowrap',
                NUMERIC.includes(types[c]) ? 'text-right' : 'text-left')}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, i) => (
            <tr key={i}>
              {data.columns.map((c) => {
                const text = cellText(row[c], types[c]);
                return (
                  <td key={c} title={text.length > 40 ? text : undefined}
                    className={clsx('px-3 py-2 border-b border-slate-100 text-slate-700 align-top', NUMERIC.includes(types[c]) ? 'text-right tabular-nums whitespace-nowrap' : 'max-w-[18rem]',
                      types[c] === 'date' && 'whitespace-nowrap')}>
                    <span className={clsx(!NUMERIC.includes(types[c]) && 'line-clamp-2 break-words')}>{text}</span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LibrarySkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[20rem_minmax(0,1fr)] gap-5" aria-busy="true" aria-label="Loading">
      <div className="bg-white rounded-2xl border border-slate-200/80 p-4 space-y-3">{[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-11" />)}</div>
      <div className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3"><Skeleton className="h-6 w-56" />{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-8" />)}</div>
    </div>
  );
}

